import { applianceCategoryMode, EMetaModeGroup } from "@/data/api/kili/programs/program.type";
import { EProgramApplianceCategory } from "@/domains/v4b/program/program.types";
import {
  EOnProgramMetaMode,
  TProgramMetaMode,
  ECommonProgramMetaMode,
  EHeaterProgramMetaMode,
  SubprogramsByApplianceCategory,
  SubprogramRange,
  WeeklySubprogram,
} from "@/domains/v4b/program/subprogram.types";
import { EDay } from "@/utils/date";
import { MAX_STRING_TIME, MIN_STRING_TIME, stringLocalTimeSchema } from "@/utils/time";

export type TProgramQuickConfiguration = EOnOffApplianceQuickConfiguration | EHeaterQuickConfiguration;

export enum EOnOffApplianceQuickConfiguration {
  WEEKEND_OFF = "WEEKEND_OFF",
  EVERY_DAY_ON = "EVERY_DAY_ON",
  NIGHT_OFF = "NIGHT_OFF",
}

export enum EHeaterQuickConfiguration {
  BUSINESS_DAYS_ECO = "BUSINESS_DAYS_ECO",
  EVERY_DAY_COMFORT = "EVERY_DAY_COMFORT",
  NIGHT_NO_FROST = "NIGHT_NO_FROST",
}

const days = Object.values(EDay);
const quickOnOffApplianceConfigurationOrder: EOnOffApplianceQuickConfiguration[] = [
  EOnOffApplianceQuickConfiguration.EVERY_DAY_ON,
  EOnOffApplianceQuickConfiguration.NIGHT_OFF,
  EOnOffApplianceQuickConfiguration.WEEKEND_OFF,
] as const;
const quickHeaterConfigurationOrder: EHeaterQuickConfiguration[] = [
  EHeaterQuickConfiguration.EVERY_DAY_COMFORT,
  EHeaterQuickConfiguration.BUSINESS_DAYS_ECO,
  EHeaterQuickConfiguration.NIGHT_NO_FROST,
] as const;

function createRange(
  startTime: string,
  endTime: string,
  metaMode: Exclude<TProgramMetaMode, "PROGRAMMING">,
): SubprogramRange {
  return {
    startTime: stringLocalTimeSchema.parse(startTime),
    endTime: stringLocalTimeSchema.parse(endTime),
    metaMode,
    temperature: undefined,
  };
}

function getActiveMode(category: EProgramApplianceCategory): EHeaterProgramMetaMode.COMFORT | EOnProgramMetaMode.ON {
  const modeGroup = applianceCategoryMode[category];

  if (modeGroup === EMetaModeGroup.HEATER_MODE) {
    return EHeaterProgramMetaMode.COMFORT;
  }

  if (modeGroup === EMetaModeGroup.ON_OFF_MODE || modeGroup === EMetaModeGroup.ON_OFF_TEMP_MODE) {
    return EOnProgramMetaMode.ON;
  }

  throw new Error(`Unsupported program appliance category: ${category}`);
}

function createWeeklyProgram(createRanges: (day: EDay) => SubprogramRange[]): WeeklySubprogram {
  return days.reduce<WeeklySubprogram>((program, day) => {
    program[day] = createRanges(day).map((range) => ({ ...range }));
    return program;
  }, {} as WeeklySubprogram);
}

function haveSameMode(firstRange: SubprogramRange, secondRange: SubprogramRange): boolean {
  if (firstRange.metaMode !== secondRange.metaMode) return false;

  if (firstRange.metaMode === ECommonProgramMetaMode.TEMP && secondRange.metaMode === ECommonProgramMetaMode.TEMP) {
    return firstRange.temperature === secondRange.temperature;
  }

  return true;
}

function mergeAdjacentRanges(ranges: SubprogramRange[]): SubprogramRange[] {
  return ranges.reduce<SubprogramRange[]>((mergedRanges, range) => {
    const previousRange = mergedRanges.at(-1);

    if (previousRange && previousRange.endTime === range.startTime && haveSameMode(previousRange, range)) {
      previousRange.endTime = range.endTime;
      return mergedRanges;
    }

    mergedRanges.push({ ...range });
    return mergedRanges;
  }, []);
}

function applyRange(dailyProgram: SubprogramRange[], appliedRange: SubprogramRange): SubprogramRange[] {
  const remainingRanges = dailyProgram.flatMap<SubprogramRange>((range) => {
    if (range.endTime <= appliedRange.startTime || range.startTime >= appliedRange.endTime) {
      return [{ ...range }];
    }

    const splitRanges: SubprogramRange[] = [];

    if (range.startTime < appliedRange.startTime) {
      splitRanges.push({ ...range, endTime: appliedRange.startTime });
    }

    if (range.endTime > appliedRange.endTime) {
      splitRanges.push({ ...range, startTime: appliedRange.endTime });
    }

    return splitRanges;
  });

  return mergeAdjacentRanges(
    [...remainingRanges, { ...appliedRange }].sort((firstRange, secondRange) =>
      firstRange.startTime.localeCompare(secondRange.startTime),
    ),
  );
}

function mapProgram(
  program: WeeklySubprogram,
  mapRanges: (ranges: SubprogramRange[]) => SubprogramRange[],
): WeeklySubprogram {
  return days.reduce<WeeklySubprogram>((updatedProgram, day) => {
    updatedProgram[day] = mapRanges(program[day]).map((range) => ({ ...range }));
    return updatedProgram;
  }, {} as WeeklySubprogram);
}

function applyWeekendOffProgram(program: WeeklySubprogram): WeeklySubprogram {
  return days.reduce<WeeklySubprogram>((updatedProgram, day) => {
    updatedProgram[day] =
      day === EDay.SATURDAY || day === EDay.SUNDAY
        ? [createRange(MIN_STRING_TIME, MAX_STRING_TIME, ECommonProgramMetaMode.OFF)]
        : program[day].map((range) => ({ ...range }));
    return updatedProgram;
  }, {} as WeeklySubprogram);
}

function applyEveryDayOnProgram(program: WeeklySubprogram): WeeklySubprogram {
  return mapProgram(program, (ranges) =>
    mergeAdjacentRanges(
      ranges.map((range) =>
        range.metaMode === ECommonProgramMetaMode.OFF
          ? createRange(range.startTime, range.endTime, EOnProgramMetaMode.ON)
          : range,
      ),
    ),
  );
}

function applyEverydayComfortProgram(program: WeeklySubprogram): WeeklySubprogram {
  return mapProgram(program, (ranges) =>
    mergeAdjacentRanges(
      ranges.map((range) => createRange(range.startTime, range.endTime, EHeaterProgramMetaMode.COMFORT)),
    ),
  );
}

function applyNightModeProgram(
  program: WeeklySubprogram,
  mode: Exclude<TProgramMetaMode, "PROGRAMMING">,
): WeeklySubprogram {
  return mapProgram(program, (ranges) => {
    const withMorningOff = applyRange(ranges, createRange(MIN_STRING_TIME, "06:00", mode));

    return applyRange(withMorningOff, createRange("22:00", MAX_STRING_TIME, mode));
  });
}

function applyBusinessDaysEcoProgram(program: WeeklySubprogram): WeeklySubprogram {
  return days.reduce<WeeklySubprogram>((updatedProgram, day) => {
    updatedProgram[day] =
      day !== EDay.SATURDAY && day !== EDay.SUNDAY
        ? [createRange(MIN_STRING_TIME, MAX_STRING_TIME, EHeaterProgramMetaMode.ECO)]
        : program[day].map((range) => ({ ...range }));
    return updatedProgram;
  }, {} as WeeklySubprogram);
}

export function createOffProgram(): WeeklySubprogram {
  return createWeeklyProgram(() => [createRange(MIN_STRING_TIME, MAX_STRING_TIME, ECommonProgramMetaMode.OFF)]);
}

function createDefaultProgram(category: EProgramApplianceCategory): WeeklySubprogram {
  const activeMode = getActiveMode(category);
  const inactiveMode =
    applianceCategoryMode[category] === EMetaModeGroup.HEATER_MODE
      ? EHeaterProgramMetaMode.ECO
      : ECommonProgramMetaMode.OFF;

  return createWeeklyProgram(() => [
    createRange(MIN_STRING_TIME, "06:00", inactiveMode),
    createRange("06:00", "22:00", activeMode),
    createRange("22:00", MAX_STRING_TIME, inactiveMode),
  ]);
}

export function createDefaultSubPrograms(
  categories: readonly EProgramApplianceCategory[],
): SubprogramsByApplianceCategory {
  return categories.reduce<SubprogramsByApplianceCategory>((programs, category) => {
    programs[category] = createDefaultProgram(category);
    return programs;
  }, {} as SubprogramsByApplianceCategory);
}

function applyQuickConfigurationProgram(
  program: WeeklySubprogram,
  configuration: TProgramQuickConfiguration,
): WeeklySubprogram {
  switch (configuration) {
    case EHeaterQuickConfiguration.BUSINESS_DAYS_ECO:
      return applyBusinessDaysEcoProgram(program);
    case EHeaterQuickConfiguration.EVERY_DAY_COMFORT:
      return applyEverydayComfortProgram(program);
    case EHeaterQuickConfiguration.NIGHT_NO_FROST:
      return applyNightModeProgram(program, EHeaterProgramMetaMode.NO_FROST);
    case EOnOffApplianceQuickConfiguration.WEEKEND_OFF:
      return applyWeekendOffProgram(program);
    case EOnOffApplianceQuickConfiguration.EVERY_DAY_ON:
      return applyEveryDayOnProgram(program);
    case EOnOffApplianceQuickConfiguration.NIGHT_OFF:
      return applyNightModeProgram(program, ECommonProgramMetaMode.OFF);
  }
}

export function applyQuickConfigurationsProgram(
  program: WeeklySubprogram,
  category: EProgramApplianceCategory,
  configurations: readonly TProgramQuickConfiguration[],
): WeeklySubprogram {
  const clonedProgram = createWeeklyProgram((day) => program[day]);

  let quickConfigurationOrder: TProgramQuickConfiguration[] = quickOnOffApplianceConfigurationOrder;

  if (applianceCategoryMode[category] === EMetaModeGroup.HEATER_MODE) {
    quickConfigurationOrder = quickHeaterConfigurationOrder;
  }

  return quickConfigurationOrder.reduce(
    (updatedProgram, configuration) =>
      configurations.includes(configuration)
        ? applyQuickConfigurationProgram(updatedProgram, configuration)
        : updatedProgram,
    clonedProgram,
  );
}
